import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { logger } from '@/lib/logger';
import { Home, ArrowLeft, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Logo } from "@/components/Logo";
import heroGradient from "@/assets/hero-gradient.jpg";
import northLogoWhite from "@/assets/north-logo-white.png";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    logger.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Hero Gradient Background */}
      <div
        className="absolute inset-0 -z-10 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${heroGradient})`,
        }}
      >
        {/* Overlay for better text contrast */}
        <div className="absolute inset-0 bg-background/40 backdrop-blur-sm" />
      </div>

      {/* Main Content */}
      <div className="flex min-h-screen flex-col items-center justify-center px-4 py-16">
        <div className="w-full max-w-2xl">
          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <Logo customLogoUrl={northLogoWhite} size="lg" />
          </div>

          {/* Main 404 Card */}
          <Card className="relative overflow-hidden border-2 p-8 text-center shadow-lg md:p-12">
            {/* Decorative gradient overlay */}
            <div
              className="absolute right-0 top-0 h-32 w-32 opacity-10 blur-2xl"
              style={{
                background: 'linear-gradient(135deg, hsl(190 95% 55%), hsl(270 85% 65%))'
              }}
            />

            {/* Content */}
            <div className="relative z-10">
              {/* Animated 404 Number */}
              <div className="mb-6">
                <h1
                  className="bg-gradient-to-r from-[hsl(190_95%_45%)] via-[hsl(220_90%_60%)] to-[hsl(270_85%_65%)] bg-clip-text text-9xl font-bold leading-none tracking-tight text-transparent md:text-[12rem]"
                  style={{
                    animation: 'fade-in 0.5s ease-out'
                  }}
                >
                  404
                </h1>
              </div>

              {/* Icon with gradient background */}
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[hsl(190_95%_55%)] via-[hsl(220_90%_60%)] to-[hsl(270_85%_65%)] opacity-90">
                <X className="h-12 w-12 text-white" strokeWidth={2.5} />
              </div>

              {/* Message */}
              <h2 className="mb-3 text-2xl font-semibold tracking-tight md:text-3xl">
                Lost in the listing?
              </h2>
              <p className="mb-8 text-lg text-muted-foreground">
                This page seems to have moved off the market. Let's get you back on track.
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                <Button
                  asChild
                  size="lg"
                  className="gap-2"
                >
                  <Link to="/">
                    <Home className="h-5 w-5" />
                    Return Home
                  </Link>
                </Button>

                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="gap-2"
                  onClick={() => window.history.back()}
                >
                  <button>
                    <ArrowLeft className="h-5 w-5" />
                    Go Back
                  </button>
                </Button>
              </div>

              {/* Helpful Links */}
              <div className="mt-8 border-t pt-6">
                <p className="mb-4 text-sm font-medium text-muted-foreground">
                  Popular destinations:
                </p>
                <div className="flex flex-wrap justify-center gap-3 text-sm">
                  <Link
                    to="/dashboard"
                    className="text-primary hover:underline"
                  >
                    Dashboard
                  </Link>
                  <span className="text-muted-foreground">•</span>
                  <Link
                    to="/signup"
                    className="text-primary hover:underline"
                  >
                    Sign Up
                  </Link>
                  <span className="text-muted-foreground">•</span>
                  <Link
                    to="/settings"
                    className="text-primary hover:underline"
                  >
                    Settings
                  </Link>
                </div>
              </div>
            </div>
          </Card>

          {/* Additional Help */}
          <div className="mt-6 text-center">
            <p className="text-sm text-white">
              If you believe this is an error, please{" "}
              <a
                href="mailto:support@northreports.com"
                className="text-white hover:underline"
              >
                contact support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
